import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Check, Mail, Phone, MapPin, Calendar, Package, ArrowRight, Loader2, AlertTriangle, CreditCard } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 12;

export default function Confirmation() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [booking, setBooking] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(sessionId ? "checking" : "n/a");
  const [err, setErr] = useState(false);
  const pollsRef = useRef(0);

  // Poll Stripe status if returned from checkout
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setPaymentStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setPaymentStatus("expired");
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setPaymentStatus("timeout");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setPaymentStatus("error");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Fetch booking (retry once after payment confirms so status flips)
  useEffect(() => {
    let cancelled = false;
    const fetchBooking = async () => {
      try {
        const { data } = await api.get(`/bookings/${code}`);
        if (!cancelled) setBooking(data);
      } catch {
        if (!cancelled) setErr(true);
      }
    };
    fetchBooking();
    return () => { cancelled = true; };
  }, [code, paymentStatus]);

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p>Booking not found.</p>
        <Link to="/" className="bg-[#FF4F00] text-white px-6 py-3 font-semibold">Home</Link>
      </div>
    );
  }
  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF4F00]" />
      </div>
    );
  }

  const isPending = paymentStatus === "checking";
  const isPaid = paymentStatus === "paid" || (booking.status === "confirmed" && sessionId);
  const isFailed = !isPaid && ["expired", "timeout", "error"].includes(paymentStatus);

  return (
    <div className="bg-[#F8F8F8] min-h-screen" data-testid="confirmation-page">
      <Nav />
      <section className="pt-24 md:pt-28 pb-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 lg:px-16">

          {sessionId && isPending && (
            <div className="bg-white border border-neutral-200 p-6 mb-6 flex items-center gap-4" data-testid="payment-checking">
              <Loader2 className="w-5 h-5 animate-spin text-[#FF4F00]" />
              <div>
                <p className="font-semibold">Verifying your deposit…</p>
                <p className="text-sm text-neutral-500">This usually takes just a few seconds.</p>
              </div>
            </div>
          )}

          {isFailed && (
            <div className="bg-white border border-red-300 p-6 mb-6 flex items-start gap-4" data-testid="payment-failed">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-700">We couldn&apos;t verify your payment.</p>
                <p className="text-sm text-neutral-600 mt-1">
                  Your booking is on hold. Try paying again or reach out to wc2389@yahoo.com.
                </p>
                <Link
                  to={`/book/${booking.tier_id}`}
                  className="inline-flex items-center gap-2 mt-3 text-[#FF4F00] font-semibold text-sm"
                >
                  Retry payment <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          <div className="bg-[#0A0A0A] text-white p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#FF4F00] opacity-20 blur-3xl" />
            <div className="relative">
              <div className="w-14 h-14 bg-[#FF4F00] inline-flex items-center justify-center mb-6">
                {isPaid ? <Check className="w-8 h-8" strokeWidth={3} /> : <CreditCard className="w-7 h-7" />}
              </div>
              <span className="text-xs tracking-[0.25em] uppercase text-neutral-400 font-semibold">
                {isPaid ? "Confirmed" : "Awaiting payment"}
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-[0.95]">
                {isPaid ? `You're booked, ${booking.full_name.split(" ")[0]}.` : "Almost there."}
              </h1>
              <p className="mt-4 text-neutral-300 max-w-lg">
                {isPaid
                  ? `Deposit received and your card is safely on file. Your totes arrive on day one of your window, and we'll automatically charge the $${booking.balance_due} balance on delivery day — no extra step from you.`
                  : "Your dates are held pending deposit confirmation."}
              </p>

              <div className="mt-8 inline-block bg-white text-[#0A0A0A] px-6 py-4">
                <p className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">Confirmation code</p>
                <p className="font-display text-2xl font-bold mt-1" data-testid="confirmation-code">{booking.confirmation_code}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 border-t-0 p-10 md:p-16">
            {booking.status === "confirmed" && booking.stripe_customer_id && (
              <Link
                to={`/upsell/${booking.confirmation_code}`}
                data-testid="upsell-cta"
                className="mb-8 flex items-center justify-between gap-4 bg-[#FF4F00]/5 border border-[#FF4F00]/30 p-5 hover:bg-[#FF4F00]/10 transition-colors group"
              >
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-[#FF4F00] font-semibold">
                    Need a little more?
                  </p>
                  <p className="font-display text-lg md:text-xl font-bold mt-1">
                    Add extra totes, dollies or a wardrobe box
                  </p>
                  <p className="text-sm text-neutral-600 mt-1">
                    Charged to your saved card — no re-entry needed.
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#FF4F00] group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
            <h2 className="font-display text-2xl font-bold mb-8">Booking summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-neutral-200 border border-neutral-200">
              <Detail icon={<Package className="w-4 h-4"/>} label="Tier" value={`${booking.tier_name} · ${booking.tote_count} totes`} />
              <Detail icon={<Calendar className="w-4 h-4"/>} label="Window" value={`${booking.start_date} → ${booking.end_date}`} />
              <Detail icon={<Mail className="w-4 h-4"/>} label="Email" value={booking.email} />
              <Detail icon={<Phone className="w-4 h-4"/>} label="Phone" value={booking.phone} />
              <Detail icon={<MapPin className="w-4 h-4"/>} label="Delivery" value={booking.delivery_address} fullWidth />
            </div>

            {booking.damage_protection && (
              <div className="mt-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 p-4" data-testid="damage-protection-indicator">
                <Check className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Damage protection active</p>
                  <p className="text-xs text-emerald-700">Covers minor incidents (broken lids, dolly wheels, straps). Cracked/missing tote and dolly replacement fees still apply.</p>
                </div>
              </div>
            )}

            <div className="mt-8 bg-[#F8F8F8] border border-neutral-200 p-6 md:p-8 flex flex-col md:flex-row gap-6 md:items-center justify-between">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between md:gap-16">
                  <span className="text-neutral-500">Deposit {isPaid ? "paid" : "due"}</span>
                  <span className="font-semibold" data-testid="paid-deposit">${booking.deposit}</span>
                </div>
                <div className="flex justify-between md:gap-16">
                  <span className="text-neutral-500">Balance on delivery (auto-charged)</span>
                  <span className="font-semibold" data-testid="paid-balance">${booking.balance_due}</span>
                </div>
                {booking.addon_total > 0 && (
                  <div className="flex justify-between md:gap-16">
                    <span className="text-neutral-500">Add-ons</span>
                    <span className="font-semibold text-[#FF4F00]" data-testid="addon-total">
                      + ${booking.addon_total}
                    </span>
                  </div>
                )}
                <div className="flex justify-between md:gap-16 pt-2 border-t border-neutral-300 mt-2">
                  <span className="font-semibold">Total</span>
                  <span className="font-display text-xl font-bold">
                    ${booking.price + (booking.addon_total || 0)}
                  </span>
                </div>
              </div>
              <Link
                to="/"
                data-testid="back-home"
                className="inline-flex items-center gap-2 bg-[#FF4F00] text-white px-7 py-4 font-semibold tracking-wide hover:bg-[#E64700] transition-colors"
              >
                Back to home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Detail = ({ icon, label, value, fullWidth }) => (
  <div className={`bg-white p-6 ${fullWidth ? "md:col-span-2" : ""}`}>
    <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-neutral-500 font-semibold">
      {icon} {label}
    </div>
    <p className="mt-2 font-medium text-[#0A0A0A]">{value}</p>
  </div>
);
